import React from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import { environment } from './Environment';
import Index from './components/Landing/Index';
import NavigationBar from './components/Landing/Navigation/NavigationBar';
import Catalogue from './components/Catalogue';
import Account from './components/Account';
import SignIn from './components/Session/SignIn';
import SignUp from './components/Session/SignUp';
import PrivateRoute from './PrivateRoute';
import ForgotPassword from './components/Session/ForgotPassword';
import ResetPassword from "./components/Session/ResetPassword";
import TermsOfUse from "./components/Landing/TermsOfUse";
import PrivacyPolicy from "./components/Landing/PrivacyPolicy";
import { LIBRARY, PROGRAM, inProgramme } from './helpers/accounts';
import { themeFor } from './theme';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-toastify/dist/ReactToastify.min.css';

// On an entrance the path decides the colour, so the library sign-in does not
// wear the programme's colours while a programme session is still open.
// Everywhere else it is the account in use that decides.
const LIBRARY_ENTRANCES = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password'];

const scopeFor = pathname => {
    if (pathname.startsWith('/program')) return PROGRAM;
    if (LIBRARY_ENTRANCES.includes(pathname)) return LIBRARY;

    return inProgramme() ? PROGRAM : LIBRARY;
};

const AccountTheme = ({ children }) => {
    const location = useLocation();

    return <ThemeProvider theme={themeFor(scopeFor(location.pathname))}>{children}</ThemeProvider>;
};

// Signing in or out in another tab changes the session this one is showing.
// Rebuild the page rather than leave it displaying an account that is no
// longer signed in.
const FollowSessionChanges = () => {
    React.useEffect(() => {
        const onStorage = event => {
            if (event.key === null || event.key === 'account') window.location.reload();
        };

        window.addEventListener('storage', onStorage);

        return () => window.removeEventListener('storage', onStorage);
    }, []);

    return null;
};

const App = () => {
    return (
        <RelayEnvironmentProvider environment={environment}>
            <Router>
              <AccountTheme>
                <FollowSessionChanges />
                <ToastContainer />
                <NavigationBar />
                <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/sign-in" element={<SignIn />} />
                    <Route path="/program/sign-in" element={<SignIn scope={PROGRAM} />} />
                    <Route path="/program/forgot-password" element={<ForgotPassword scope={PROGRAM} />} />
                    <Route path="/sign-up" element={<SignUp />} />
                    <Route path="/terms-of-use" element={<TermsOfUse />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/catalogue/:category" element={<Catalogue />} />
                    <Route path="/catalogue" element={<Navigate to="/catalogue/full-collection" replace />} />

                    <Route
                        path="/account/*"
                        element={
                            <PrivateRoute>
                                <Account />
                            </PrivateRoute>
                        }
                    />

                    {/* Catch-all route for unknown paths, redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AccountTheme>
            </Router>
        </RelayEnvironmentProvider>
    );
}

export default App;
